/**
 * 宿主 OS（Electron webview / BrowserWindow preload 注入）。
 * 生命周期内不变，缓存一次即可。
 */
export type HostPlatform = "darwin" | "win32" | "linux" | "unknown";

let _platformCache: HostPlatform | null = null;

const normalize = (raw: unknown): HostPlatform => {
  const v = String(raw || "").toLowerCase();
  if (v === "darwin" || v === "mac" || v === "macos") return "darwin";
  if (v === "win32" || v === "windows") return "win32";
  if (v === "linux") return "linux";
  return "unknown";
};

const detectHostPlatform = (): HostPlatform => {
  if (typeof window === "undefined") return "unknown";
  const w = window as Window & {
    hostPlatform?: { os?: string };
    process?: { platform?: string };
    electronAPI?: { platform?: string };
    electronBridge?: { platform?: string };
  };

  // 1) webview-preload: hostPlatform.os / electronBridge.platform
  if (w.hostPlatform?.os) return normalize(w.hostPlatform.os);
  if (w.electronBridge?.platform) return normalize(w.electronBridge.platform);
  // 2) main preload: process.platform / electronAPI.platform
  if (w.process?.platform) return normalize(w.process.platform);
  if (w.electronAPI?.platform) return normalize(w.electronAPI.platform);

  // 3) UA 兜底（非 Electron 浏览器不会走到窗口控件分支）
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/Mac OS X|Macintosh/i.test(ua)) return "darwin";
  if (/Windows/i.test(ua)) return "win32";
  if (/Linux/i.test(ua)) return "linux";
  return "unknown";
};

export const getHostPlatform = (): HostPlatform => {
  if (_platformCache === null) {
    _platformCache = detectHostPlatform();
  }
  return _platformCache;
};

export const useHostPlatform = (): HostPlatform => getHostPlatform();

export const useIsMacOS = (): boolean => getHostPlatform() === "darwin";

export const getIsMacOS = (): boolean => getHostPlatform() === "darwin";
